import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DeviceDetectionService } from 'app/modules/shared/services/device-detection.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { CalendarModule } from 'primeng/calendar';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CommonBindingDataService } from '../../../services/common-binding-data.service';
import * as dayjs from 'dayjs';
import 'dayjs/plugin/utc';
import 'dayjs/plugin/timezone';
import { AppSettings } from 'app/modules/shared/app.settings';

@Component({
  selector: 'app-date-time',
  templateUrl: './date-time.component.html',
  styleUrl: './date-time.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, CalendarModule, InputSwitchModule, TranslateModule]
})
export class DateTimeComponent implements OnInit {
  @Output() onSelectedDate: EventEmitter<any> = new EventEmitter();
  @Input() field: any;
  formName: FormGroup;
  @Input() attributeLabels: [];
  @Input() moduleName: string;
  @Input() isEdit: boolean;
  dateFormat = this.metaDataService.lowerDateFormat;
  attributeValueOfBookingMinTime = this.metaDataService.attributeValueOfBookingMinTime;
  HOUR_MINUTE: any = this.metaDataService.timeHourFormat;
  pickupTimeToShow;
  isTouchDevice: boolean = false;
  placeholder: any;
  switchValue: boolean = false;
  timeValue;
  isPickupTimeDisable: boolean = true;
  defaultDate: Date = this.metaDataService.getCurrentTimeInTimezone();
  minDate: Date;
  rangeDates: Date[] | undefined;
  hourFormat = this.metaDataService.timeFormat;
  isRideNowOnEdit = false;

  constructor(private formGroupDirective: FormGroupDirective,
    public cs: CommonBindingDataService,
    private metaDataService: MetaDataService,
    private deviceDetectionService: DeviceDetectionService) {
    this.formName = this.formGroupDirective.control;
  }

  ngOnInit(): void {
    this.isTouchDevice = this.deviceDetectionService.isTouchDevice();
    const selectedTimezone = this.metaDataService.getSelectedTimezone();
    const editValue = this.getEditValue();

    if (editValue) {
      this.handleEditValue(editValue, selectedTimezone);
    } else {
      this.setDefaultValue();
    }
    this.setPlaceholder();
    this.initializeDateTime();
    if (!this.isEdit) {
      const event = {
        currentTarget: {
          checked: true
        }
      }
      this.onSwitchChange(event);
    } else {
      this.isRideNowOnEdit = this.formName.controls.ride_now.value;
    }
    if (this.moduleName === AppSettings.ENTITY_CODE.BOOKING) {
      this.dateFormat = this.metaDataService.lowerDateFormat;
    }
  }

  private getEditValue() {
    return this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].value;
  }

  private handleEditValue(editValue: any, selectedTimezone: string): void {
    if (this.field.attributeCode === 'pickup_date') {
      this.setPickupDate(editValue, selectedTimezone);
    } else if (this.field.attributeCode === 'pickup_time') {
      this.setPickupTime(editValue, selectedTimezone);
    } else {
      this.setFormControlValue(new Date(editValue));
    }
  }

  private setPickupTime(editValue: any, selectedTimezone: string): void {
    let timeOnly: any = dayjs(editValue).tz(selectedTimezone, true).format('HH:mm');
    if (this.isEdit) {
      const date = new Date(this.formName.controls.pickup_date.value);
      date.setHours(timeOnly.split(':')[0]);
      date.setMinutes(timeOnly.split(':')[1]);
      timeOnly = date;
    }
    this.setFormControlValue(timeOnly);
  }

  private setPickupDate(editValue: any, selectedTimezone: string): void {
    const editDate = dayjs(editValue).tz(selectedTimezone, true).toDate();
    if (editDate < new Date()) {
      this.minDate = editDate;
    }
    this.setFormControlValue(editDate);
  }

  private setFormControlValue(value: any): void {
    const controlName = this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode;
    this.formName.controls[controlName].setValue(value);
    if (this.isEdit && this.formName.controls.ride_now.value && controlName === 'pickup_time') {
      this.formName.controls[controlName].disable();
    }
  }

  private setDefaultValue(): void {
    const currentDateInTimezone = this.metaDataService.getCurrentTimeInTimezone();
    if (this.field.attributeCode !== 'arrival_time' && !this.isEdit) {
      this.setFormControlValue(currentDateInTimezone);
      this.minDate = new Date();
      if (this.field.attributeCode === 'pickup_date') {
        const pickupDate = new Date(currentDateInTimezone);
        pickupDate.setHours(12, 0, 0, 0);
        this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue(pickupDate);
      }
    }
  }

  setPlaceholder(): void {
    this.placeholder = this.cs.getLabelValue(`${this.moduleName}.fields.${this.field.attributeCode}.label`, this.attributeLabels, null);
    if (this.placeholder === this.cs.getLabel('lbl_return_time_date')) {
      this.placeholder = this.placeholder.replace('Time & ', '');
    }
    if (this.moduleName === 'booking_activity') {
      if (this.placeholder === 'Time & Date') {
        this.placeholder = this.placeholder.replace('Time & Date', 'Date');
      }
    }
  }


  initializeDateTime(): void {
    if (this.field.attributeCode === "pickup_date") {
      const timeVa = new Date();
      timeVa.setHours(timeVa.getHours() + 2);
      timeVa.setMinutes(timeVa.getMinutes());
      timeVa.setSeconds(0);
      this.timeValue = timeVa;
      this.defaultDate = new Date();
      if (!this.isEdit) {
        this.formName.controls[this.field.attributeCode].setValue(this.defaultDate);
      }
    }
    if (this.field.attributeCode === 'incident_complaint_date') {
      this.formName.controls[this.field.attributeCode].setValue(this.formName.controls.incident_complaint_date.value);
    }
  }

  onDateTimeSelect(event) {
    const value = event;
    this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue(value);
    this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].updateValueAndValidity();
  }

  onDateSelect(event, type?) {
    const value = event;
    if (type === 'date') {
      const selectedPickUpTime = this.formName.controls.pickup_time.value;
      const selectedDate = value;
      selectedDate.setHours(selectedPickUpTime.getHours());
      selectedDate.setMinutes(selectedPickUpTime.getMinutes());


      if (this.field.attributeCode === 'departure_time_and_date') {
        this.minDate = this.formName.controls.return_time_and_date.value || new Date();
      }
      this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue(value);
      this.formName.controls.pickup_time.setValue(selectedDate);
      this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].updateValueAndValidity();
    } else if (type === 'time') {
      const selectedTime = value;
      const selectedDate = this.formName.controls.pickup_date?.value;

      if (selectedDate) {
        selectedDate.setHours(selectedTime.getHours());
        selectedDate.setMinutes(selectedTime.getMinutes());
        selectedDate.setSeconds(0);

        this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue(selectedDate);
        this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].updateValueAndValidity();
      }
    } else {
      this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue(value);
      this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].updateValueAndValidity();
    }

    const departDate = this.formName.controls.departure_time_and_date?.value;
    const returnDate = this.formName.controls.return_time_and_date?.value;
    if (departDate && returnDate) {
      const differenceInMilliseconds = Math.abs(returnDate - departDate);
      const differenceInHours = Math.floor(differenceInMilliseconds / (1000 * 60 * 60));
      const differenceInMinutes = Math.floor((differenceInMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
      this.formName.controls.journey_time.setValue(`${differenceInHours} hours ${differenceInMinutes} minutes`);
      this.formName.controls.journey_time.updateValueAndValidity();
    }
  }

  setErrorForPickupTime(value) {
    if (value && this.timeValue && (value < this.timeValue)) {
      this.formName.get('pickup_time').setValidators(Validators.required);
      return this.cs.getLabel('lbl_pickup_time_greater') + dayjs(this.pickupTimeToShow).format(AppSettings.HOUR_MINUTE);
    } else {
      return '';
    }
  }

  onDateRangeSelect() {
    if (this.rangeDates[0] && this.rangeDates[1]) {
      this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue(this.rangeDates);
      this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].updateValueAndValidity();
    }
  }

  onSwitchChange(event) {
    this.switchValue = event?.currentTarget?.checked;
    const timeFromAttribute = this.cs.convertSecondsToTime(this.attributeValueOfBookingMinTime);
    if (!this.switchValue) {
      this.isPickupTimeDisable = true;
      const currentTimeOfSelectedTimezone = this.metaDataService.getCurrentTimeInTimezone();
      currentTimeOfSelectedTimezone.setHours(currentTimeOfSelectedTimezone.getHours());
      currentTimeOfSelectedTimezone.setMinutes(currentTimeOfSelectedTimezone.getMinutes());
      currentTimeOfSelectedTimezone.setSeconds(0);
      this.timeValue = currentTimeOfSelectedTimezone;
      this.formName?.controls[this.field?.attributeCode]?.setValue(this.timeValue);
      this.formName?.controls.ride_now?.setValue(!this.switchValue);
      this.formName?.controls[this.field?.attributeCode]?.updateValueAndValidity();
    } else {
      this.isPickupTimeDisable = false;
      const currentTimeOfSelectedTimezone = this.metaDataService.getCurrentTimeInTimezone();
      currentTimeOfSelectedTimezone.setHours(currentTimeOfSelectedTimezone.getHours() + timeFromAttribute.getHours());
      currentTimeOfSelectedTimezone.setMinutes(currentTimeOfSelectedTimezone.getMinutes() + timeFromAttribute.getMinutes());
      currentTimeOfSelectedTimezone.setSeconds(0);
      this.timeValue = currentTimeOfSelectedTimezone;
      this.pickupTimeToShow = currentTimeOfSelectedTimezone;
      this.formName?.controls[this.field?.attributeCode]?.setValue(this.timeValue);
      this.formName?.controls.ride_now?.setValue(!this.switchValue);
      this.formName?.controls[this.field?.attributeCode]?.updateValueAndValidity();
    }
  }
}
